/**
 * Article File Search API
 * 
 * Wrapper for searching article files using Elasticsearch
 */
import { v1Api } from '../../../api/generatedClient'
import type { APIArticleFileSearchResponse } from '../../../generated-types/models/APIArticleFileSearchResponse'
import type { V1SearchItemsArticleFilesRetrieveRequest } from '../../../generated-types/apis/V1Api'
import { createFileInfo } from '../../../utils/functions'
import type { FileInfo } from '../../../utils/types'

export interface ArticleFileSearchFilters {
  q: string
  articleId?: number
  articleNumber?: string
  articleName?: string
  fileName?: string
  storedAt?: string // YYYY-MM-DD
  fileSize?: string // File size (e.g., '10287', '10.05', '10.05 KB', '1.5 MB')
  fields?: string[]
  size?: number
  from?: number
}

export interface ArticleFileSearchResponse {
  total: number
  max_score: number | null
  results: FileInfo[]
  page: {
    from: number
    size: number
    has_more: boolean
  }
}

function toArticleFileSearchRequest(
  filters: ArticleFileSearchFilters
): V1SearchItemsArticleFilesRetrieveRequest {
  if (!filters.q || filters.q.trim() === '') {
    throw new Error('Search query (q) is required')
  }

  return {
    q: filters.q,
    fields: filters.fields && filters.fields.length > 0 ? filters.fields.join(',') : undefined,
    size: filters.size,
    from: filters.from,
    articleId: filters.articleId,
    articleNumber: filters.articleNumber,
    articleName: filters.articleName,
    fileName: filters.fileName,
    storedAt: filters.storedAt ? new Date(filters.storedAt) : undefined,
    fileSize: filters.fileSize,
  }
}


/**
 * Search for article files using Elasticsearch
 * 
 * @param filters - Search filters
 * @returns Article file search response with results and pagination info
 */
export async function searchArticleFiles(
  filters: ArticleFileSearchFilters
): Promise<ArticleFileSearchResponse> {
  const response: APIArticleFileSearchResponse = await v1Api.v1SearchItemsArticleFilesRetrieve(
    toArticleFileSearchRequest(filters)
  )

  // Map results to FileInfo
  const fileInfos = response.results.map((result) => {
    // Create a FileInfo-like object from the search result
    const fileInfo: Record<string, unknown> = {
      self: `/v1/items/articles/${result.articleId ?? 'unknown'}/files/${result.id}/`,
      name: result.fileName ?? '',
      key: result.key ?? '',
      size: result.fileSize ?? 0,
      stored_at: result.storedAt?.toISOString(),
      last_modified_at: result.lastModifiedAt?.toISOString(),
    }

    if (result.articleId) {
      fileInfo.article = `/v1/items/articles/${result.articleId}/`
    }

    return createFileInfo(fileInfo)
  })

  return {
    total: response.total,
    max_score: response.maxScore,
    results: fileInfos,
    page: {
      from: response.page.fromValue,
      size: response.page.size,
      has_more: response.page.hasMore,
    },
  }
}
